import { TYPES } from '@Types';
import {
  ArsenalUnitStepRepository,
  RequestArsenalUnitStepSuccess,
} from '@Repository/arsenal';
import { inject, injectable } from 'inversify';
import {
  ArsenalArmy,
  ArsenalUnit,
  ArsenalUnitStep,
  SyncEntityState,
} from '@Models';
import { ILoadingEntityAction } from '@Redux/ILoadingEntityAction';
import arsenalArmySlice from '@Redux/reducers/arsenal/arsenal-army.slice';
import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Action, AppState } from '@Redux/store';
import { ThunkDispatch } from 'redux-thunk';

@injectable()
export class ArsenalUnitStepAction extends ILoadingEntityAction {
  @inject(TYPES.Repository.ArsenalUnitStepRepository)
  private _repository!: ArsenalUnitStepRepository;

  public create = createAsyncThunk(
    `${this.constructor.name}/create`,
    async (
      { unitId, step }: { unitId: string; step: ArsenalUnitStep },
      thunkAPI,
    ) => {
      thunkAPI.dispatch(this.areLoading(true));

      const response = await this._repository.create(unitId, step);

      const arsenal = (thunkAPI.getState() as AppState).arsenalArmy.entity!!;

      if (
        this.handleFailure(
          response,
          thunkAPI.dispatch as ThunkDispatch<AppState, undefined, Action>,
        )
      ) {
        thunkAPI.dispatch(
          this.fetchDataSuccess(
            this.updateStep(arsenal, unitId, {
              ...step,
              state: SyncEntityState.Failed,
            }),
          ),
        );
        return;
      }

      const success = response as RequestArsenalUnitStepSuccess;

      thunkAPI.dispatch(
        this.fetchDataSuccess(
          this.updateStep(arsenal, unitId, success.payload),
        ),
      );
      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  public update = createAsyncThunk(
    `${this.constructor.name}/update`,
    async (
      { unitId, step }: { unitId: string; step: ArsenalUnitStep },
      thunkAPI,
    ) => {
      thunkAPI.dispatch(this.areLoading(true));

      const response = await this._repository.update(step);

      const arsenal = (thunkAPI.getState() as AppState).arsenalArmy.entity!!;

      if (
        this.handleFailure(
          response,
          thunkAPI.dispatch as ThunkDispatch<AppState, undefined, Action>,
        )
      ) {
        thunkAPI.dispatch(
          this.fetchDataSuccess(
            this.updateStep(arsenal, unitId, {
              ...step,
              state: SyncEntityState.Failed,
            }),
          ),
        );
        return;
      }

      const success = response as RequestArsenalUnitStepSuccess;

      thunkAPI.dispatch(
        this.fetchDataSuccess(
          this.updateStep(arsenal, unitId, success.payload),
        ),
      );
      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  protected fetchDataSuccess(item: ArsenalArmy): PayloadAction<ArsenalArmy> {
    return arsenalArmySlice.actions.setArsenalArmy(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return arsenalArmySlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return arsenalArmySlice.actions.setLoading(bool);
  }

  private updateStep(
    arsenal: ArsenalArmy,
    unitId: string,
    step: ArsenalUnitStep,
  ): ArsenalArmy {
    const units = arsenal.units.map((item: ArsenalUnit) => {
      if (item.id === unitId) {
        return {
          ...item,
          steps: item.steps.map((s: ArsenalUnitStep) => {
            if (s.step === step.step) {
              return step;
            }

            return s;
          }),
        };
      }
      return item;
    });

    return {
      ...arsenal,
      units: units,
    };
  }
}
